import type { DescMethodUnary } from '@bufbuild/protobuf'
import { RealtimeQueryEnum, type SealedLiveQuery as SealedLiveQueryProto } from '@/gen/cortado/v1/realtime_pb'
import {
  getDashboardSummary,
  getInvoice,
  getJob,
  getPipelineSummary,
  getQuote,
  getQuoteDetail,
  listInquiries,
  listInvoicePayments,
  listInvoices,
  listJobs,
  listQuotes,
} from '@/gen/cortado/v1/cortado-CortadoService_connectquery'

/**
 * Live activity: Neon Realtime live queries for new leads, accepted quotes and
 * received payments. The backend owns the SQL (backend/pkg/services/livequery)
 * and hands us sealed queries; each one keeps the newest WINDOW_SIZE matching
 * rows live. A row id we have not seen before is the event.
 */

/** Mirrors livequery.WindowSize on the backend. */
export const WINDOW_SIZE = 25

/**
 * More new rows than this in one update collapse into a single summary toast.
 * A laptop waking after an hour, or a bulk import, can surface a whole window
 * of rows at once; the lists still refresh in full through invalidation.
 */
export const MAX_TOASTS_PER_UPDATE = 3

/** Row shapes. Column names match the backend SQL exactly. */
export interface LeadRow {
  id: string
  job_id: string
  created_at: unknown
  first_name: string
  last_name: string
}

export interface AcceptedQuoteRow {
  id: string
  job_id: string
  quote_number: number | string
  title: string | null
  accepted_at: unknown
}

export interface PaymentRow {
  id: string
  amount_cents: number | string | bigint
  currency: string
  created_at: unknown
}

/** The SDK's SealedLiveQuery, structurally (kept local to avoid importing the SDK here). */
export interface SealedQuery {
  capability: string
  queryFingerprint: string
  expiresAt: number
}

export function toSealedQuery(sealed: SealedLiveQueryProto): SealedQuery {
  return {
    capability: sealed.capability,
    queryFingerprint: sealed.queryFingerprint,
    expiresAt: Number(sealed.expiresAtMs),
  }
}

/** Milliseconds since epoch for a decoded Postgres timestamp, or NaN. */
export function toMillis(value: unknown): number {
  if (value instanceof Date) return value.getTime()
  if (typeof value === 'number') return value
  if (typeof value === 'string') {
    // Postgres text format: "2026-10-16 12:00:00.123456+02". Normalize to ISO
    // 8601: a "T" separator, at most millisecond precision, and a "+hh:mm"
    // offset. `timestamp without time zone` columns are stored as UTC.
    let iso = value.trim().replace(' ', 'T').replace(/(\.\d{3})\d+/, '$1')
    const offset = /([+-])(\d\d):?(\d\d)?$/.exec(iso.slice(19))
    if (offset) {
      iso = `${iso.slice(0, 19 + offset.index)}${offset[1]}${offset[2]}:${offset[3] ?? '00'}`
    } else if (!/[zZ]$/.test(iso)) {
      iso = `${iso}Z`
    }
    return Date.parse(iso)
  }
  return Number.NaN
}

/**
 * Tracks which rows of one live query are new.
 *
 * The first snapshot is the baseline and never reports anything, so loading
 * the page (or reconnecting) does not replay old activity. After that, a row
 * is new when its id is unseen and it sorts at or after the oldest baseline
 * row. Otherwise an old row re-entering the window would look new: one that
 * backfills after a recent row is deleted, or an old lead being unarchived
 * into a window that was never full. A row with no parseable time counts as
 * new, and a baseline with no parseable times sets no floor.
 */
export function createNewRowDetector<Row>(opts: {
  getId: (row: Row) => string
  getTime: (row: Row) => number
}) {
  const seen = new Set<string>()
  let baselined = false
  let floor = Number.NEGATIVE_INFINITY

  return (rows: readonly Row[]): Row[] => {
    if (!baselined) {
      baselined = true
      for (const row of rows) seen.add(opts.getId(row))
      const times = rows.map((r) => opts.getTime(r)).filter((t) => !Number.isNaN(t))
      if (times.length > 0) floor = Math.min(...times)
      return []
    }

    const fresh: Row[] = []
    for (const row of rows) {
      const id = opts.getId(row)
      if (seen.has(id)) continue
      seen.add(id)
      const time = opts.getTime(row)
      if (Number.isNaN(time) || time >= floor) fresh.push(row)
    }
    return fresh
  }
}

function formatMoney(amountCents: PaymentRow['amount_cents'], currency: string): string {
  const amount = Number(amountCents) / 100
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format(amount)
  } catch {
    return `${amount.toFixed(2)} ${currency}`
  }
}

export interface LiveToast {
  title: string
  description?: string
  /** In-app path for the toast's "View" action. */
  href?: string
}

export interface LiveFeed<Row> {
  query: RealtimeQueryEnum
  getId: (row: Row) => string
  getTime: (row: Row) => number
  toast: (row: Row) => LiveToast
  /** One toast standing in for a burst of rows (bulk import, waking from sleep). */
  summary: (count: number) => LiveToast
  /** Queries whose cached data a new row makes stale. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- request/response types vary per method
  invalidates: readonly DescMethodUnary<any, any>[]
}

const leadsFeed: LiveFeed<LeadRow> = {
  query: RealtimeQueryEnum.REALTIME_QUERY_RECENT_LEADS,
  getId: (r) => r.id,
  getTime: (r) => toMillis(r.created_at),
  toast: (r) => {
    const name = `${r.first_name ?? ''} ${r.last_name ?? ''}`.trim()
    return { title: 'New lead', description: name || undefined, href: `/pipeline/${r.job_id}` }
  },
  summary: (n) => ({ title: `${n} new leads`, href: '/pipeline' }),
  invalidates: [listInquiries, getPipelineSummary, listJobs, getDashboardSummary],
}

const acceptedQuotesFeed: LiveFeed<AcceptedQuoteRow> = {
  query: RealtimeQueryEnum.REALTIME_QUERY_RECENT_ACCEPTED_QUOTES,
  getId: (r) => r.id,
  getTime: (r) => toMillis(r.accepted_at),
  toast: (r) => ({
    title: 'Quote accepted',
    description: r.title ? `#${r.quote_number} · ${r.title}` : `Quote #${r.quote_number}`,
    href: `/pipeline/${r.job_id}`,
  }),
  summary: (n) => ({ title: `${n} quotes accepted`, href: '/pipeline' }),
  invalidates: [listQuotes, getQuote, getQuoteDetail, listJobs, getJob, getPipelineSummary, getDashboardSummary],
}

const paymentsFeed: LiveFeed<PaymentRow> = {
  query: RealtimeQueryEnum.REALTIME_QUERY_RECENT_PAYMENTS,
  getId: (r) => r.id,
  getTime: (r) => toMillis(r.created_at),
  toast: (r) => ({ title: 'Payment received', description: formatMoney(r.amount_cents, r.currency) }),
  summary: (n) => ({ title: `${n} payments received`, href: '/invoices' }),
  invalidates: [listInvoices, getInvoice, listInvoicePayments, listJobs, getJob, getDashboardSummary],
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- heterogeneous registry keyed by query
export const LIVE_FEEDS: ReadonlyMap<RealtimeQueryEnum, LiveFeed<any>> = new Map<RealtimeQueryEnum, LiveFeed<any>>([
  [leadsFeed.query, leadsFeed],
  [acceptedQuotesFeed.query, acceptedQuotesFeed],
  [paymentsFeed.query, paymentsFeed],
])
